import { IBasketItem } from '@/context/BasketStore'
import {
  KibuCareProductPricingEntry,
  KibuCareTier,
  StripeCheckoutItem,
} from '@/services/api'

export type ParentQuantityEntry = {
  productItemId: string
  productCatalogueId: string
  quantity: number
}

export type KibuCarePlanSummary = {
  total: number
  perUnit: number
  quantity: number
  currency: string
} | null

export const getTierPrice = (
  tiers: KibuCareTier[],
  quantity: number
): { amount: number; currency: string } | null => {
  if (!tiers.length || quantity < 1) {
    return null
  }

  const sorted = [...tiers].sort((a, b) => b.minQty - a.minQty)
  for (const tier of sorted) {
    if (quantity >= tier.minQty && (tier.maxQty == null || quantity <= tier.maxQty)) {
      return { amount: tier.amount, currency: tier.currency }
    }
  }

  return null
}

export const getPlanTotal = (
  productPricing: KibuCareProductPricingEntry,
  planId: string,
  quantity: number
): { total: number; perUnit: number; currency: string } | null => {
  const tiers = productPricing.prices[planId]?.tiers
  if (!tiers) {
    return null
  }

  const tier = getTierPrice(tiers, quantity)
  if (!tier) {
    return null
  }

  return {
    perUnit: tier.amount,
    total: tier.amount * quantity,
    currency: tier.currency,
  }
}

export const buildParentQuantityMap = (basketItems: IBasketItem[]): Map<string, ParentQuantityEntry> => {
  const map = new Map<string, ParentQuantityEntry>()

  basketItems.forEach((item) => {
    // Repair-part basket items should never act as Kibu Care parent products.
    if (item.colourWay.group === 'repair-part') {
      return
    }

    const productItemId = String(item.colourWay._id)
    const itemQuantity = item.quantity ?? 1
    const existing = map.get(productItemId)
    if (existing) {
      existing.quantity += itemQuantity
      return
    }

    map.set(productItemId, {
      productItemId,
      productCatalogueId: String(item.colourWay.productCatalogueId || ''),
      quantity: itemQuantity,
    })
  })

  return map
}

export const buildEligibleParentGroups = (
  basketItems: IBasketItem[],
  productPricing: KibuCareProductPricingEntry[]
): ParentQuantityEntry[] => {
  const byCatalogueId = new Map(
    productPricing.map((entry) => [String(entry.productCatalogueId), entry])
  )

  return Array.from(buildParentQuantityMap(basketItems).values()).filter((entry) =>
    byCatalogueId.has(entry.productCatalogueId)
  )
}

export const getKibuCarePlanSummary = ({
  planId,
  basketItems,
  productPricing,
}: {
  planId: string
  basketItems: IBasketItem[]
  productPricing: KibuCareProductPricingEntry[]
}): KibuCarePlanSummary => {
  const byCatalogueId = new Map(
    productPricing.map((entry) => [String(entry.productCatalogueId), entry])
  )

  const groups = buildEligibleParentGroups(basketItems, productPricing)

  let total = 0
  let quantity = 0
  let currency = ''

  groups.forEach((group) => {
    const pricing = byCatalogueId.get(group.productCatalogueId)
    if (!pricing) {
      return
    }

    const planTotal = getPlanTotal(pricing, planId, group.quantity)
    if (!planTotal) {
      return
    }

    total += planTotal.total
    quantity += group.quantity
    if (!currency) {
      currency = planTotal.currency
    }
  })

  if (!total || !quantity || !currency) {
    return null
  }

  return {
    total,
    perUnit: Math.round(total / quantity),
    quantity,
    currency,
  }
}

export const buildKibuCareCheckoutItems = ({
  selectedPlanId,
  basketItems,
  productPricing,
}: {
  selectedPlanId: string | null | undefined
  basketItems: IBasketItem[]
  productPricing: KibuCareProductPricingEntry[]
}): StripeCheckoutItem[] => {
  if (!selectedPlanId) {
    return []
  }

  const byCatalogueId = new Map(
    productPricing.map((entry) => [String(entry.productCatalogueId), entry])
  )

  const hasEligibleGroup = buildEligibleParentGroups(basketItems, productPricing)
    .some((group) => {
      const pricing = byCatalogueId.get(group.productCatalogueId)
      if (!pricing) {
        return false
      }
      return Boolean(getPlanTotal(pricing, selectedPlanId, group.quantity))
    })

  if (!hasEligibleGroup) {
    return []
  }

  return [{
    productItemId: selectedPlanId,
    quantity: 1,
  }]
}

export const isParentChildValidationError = (errors: string[] | undefined): boolean => {
  if (!errors || !errors.length) {
    return false
  }

  return errors.some((error) => {
    const normalized = error.toLowerCase()
    return normalized.includes('requires parent') || normalized.includes('cannot exceed parent quantity')
  })
}
